/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import capitalize from 'lodash/capitalize';

import { LinkIconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import Anchor from '@atlaskit/primitives/anchor';
import { token } from '@atlaskit/tokens';

import { Assignee as AssigneeComponent } from '../../../../../../../common/ui/jira-assignee';
import { getJiraEntityIcon } from '../../../../../../../common/ui/product-entity-icon';

export type SearchJiraIssueTableRowProps = {
	/** URL of the avatar icon for the project (custom icon) */
	iconUrl: string;
	/** The type of the raw Jira node (board, project, etc.) */
	type: string;
	/** The category of the Jira project or space */
	category: string;
	/** The project or space lead -- a user */
	lead: { name: string; picture: string } | null;
	/** The project key */
	projectKey: string;
	/** The project style -- this can be something like Product Discovery or Team Manage Software */
	projectStyle: string;
	/** The project type that looks similar to the project style property */
	projectType: string;
	/** The project URL */
	projectUrl: string;
	/** The enriched project title -- contains query highlight matching */
	title: ReactNode;
	/** Called when the user clicks anywhere on the row. */
	onClick?: () => void;
};

const truncatedAnchorStyle: React.CSSProperties = {
	display: 'block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
};

const styles = {
	tr: css({
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		cursor: 'pointer',

		// there is a top-level style that targets the first and last cell of any table rows
		// that removes padding so we need to override this by using a descendant selector
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& td:first-of-type': {
			paddingLeft: token('space.100'),

			// the left border is provided by the table
			borderLeftWidth: '0px',
		},

		// there is a top-level style that targets the first and last cell of any table rows
		// that removes padding so we need to override this by using a descendant selector
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& td:last-of-type': {
			paddingRight: token('space.100'),

			// the right border is provided by the table
			borderRightWidth: '0px',
		},
	}),
	tdBase: css({
		display: 'table-cell',
		boxSizing: 'border-box',
		verticalAlign: 'middle',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		overflowX: 'hidden',
		whiteSpace: 'nowrap',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		// every table cell (except the first and last) will have a right and bottom border
		borderRightWidth: token('border.width'),
		borderBottomWidth: token('border.width'),
		borderTopWidth: '0px',
		borderLeftWidth: '0px',
	}),
	tdSpace: css({
		// fixed-width breakpoints taken from Figma: https://www.figma.com/design/D6160TfMfREv8jBeAsw5ma/Blend-Search---Chat?node-id=2419-121123&t=56eOb7RsXea00pZJ-4
		'@media (min-width: 0px)': {
			width: '210px',
		},
		'@media (min-width: 1026px)': {
			width: '317px',
		},
		'@media (min-width: 1440px)': {
			width: '362px',
		},
	}),
	tdKey: css({
		'@media (min-width: 0px)': {
			width: '80px',
		},
	}),
	tdType: css({
		'@media (min-width: 0px)': {
			width: '140px',
		},
		'@media (min-width: 1026px)': {
			width: '180px',
		},
	}),
	tdLead: css({
		'@media (min-width: 0px)': {
			width: '120px',
		},
		'@media (min-width: 768px)': {
			width: '140px',
		},
		'@media (min-width: 1026px)': {
			width: '172px',
		},
	}),
	tdCategory: css({
		'@media (min-width: 0px)': {
			width: '80px',
		},
		'@media (min-width: 1026px)': {
			width: '116px',
		},
	}),
	tdSpaceUrl: css({
		'@media (min-width: 0px)': {
			width: '80px',
		},
		'@media (min-width: 1026px)': {
			width: '112px',
		},
	}),
	tdSpaceUrlInner: css({
		display: 'flex',
		flexFlow: 'row nowrap',
		alignItems: 'center',
		justifyContent: 'center',
	}),
	tdSpaceInner: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.075'),
		overflow: 'hidden',
	}),
	truncateText: css({
		display: 'block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	}),
	link: css({
		flexShrink: 0,
		color: token('color.link'),
		fontWeight: token('font.weight.regular'),
	}),
	iconWrapper: css({
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	}),
};

const makeProjectStyleOrTypeReadable = (styleOrType: string): string => {
	return styleOrType.split('_').map(capitalize).join(' ');
};

export const SearchJiraProjectTableRow = ({
	iconUrl,
	type,
	projectKey,
	title,
	projectUrl,
	projectStyle,
	projectType,
	lead,
	category,
	onClick,
}: SearchJiraIssueTableRowProps): React.JSX.Element => {
	const Icon = getJiraEntityIcon({
		iconUrl,
		type,
		isIssue: false,
		size: 'small',
	});

	return (
		<tr css={styles.tr} data-testid="search-jira-issue-table-row" onClick={onClick}>
			{/* Space column: icon + title */}
			<td css={[styles.tdBase, styles.tdSpace]}>
				<div css={styles.tdSpaceInner}>
					<span css={styles.iconWrapper}>{Icon}</span>
					{title && (
						<Anchor
							href={projectUrl}
							target="_blank"
							onClick={onClick}
							// directly pass in a style to the anchor component to truncate the inner text
							// so that we don't need additional wrapper elements to achieve the same effect
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={truncatedAnchorStyle}
						>
							{title}
						</Anchor>
					)}
				</div>
			</td>

			{/* Key column */}
			<td css={[styles.tdBase, styles.tdKey]}>
				<span css={styles.truncateText}>{projectKey || null}</span>
			</td>

			{/* Type column */}
			<td css={[styles.tdBase, styles.tdType]}>
				<span css={styles.truncateText}>
					{makeProjectStyleOrTypeReadable(projectStyle || projectType)}
				</span>
			</td>

			{/* Lead column: user avatar and name */}
			<td css={[styles.tdBase, styles.tdLead]}>
				<AssigneeComponent user={lead ?? null} />
			</td>

			{/* Category column */}
			<td css={[styles.tdBase, styles.tdCategory]}>{category}</td>

			{/* Space URL column: clickable link icon that takes the user to the project / space */}
			<td css={[styles.tdBase, styles.tdSpaceUrl]}>
				<div css={styles.tdSpaceUrlInner}>
					<LinkIconButton
						appearance="subtle"
						isTooltipDisabled={false}
						href={projectUrl}
						icon={LinkIcon}
						label={projectUrl}
					/>
				</div>
			</td>
		</tr>
	);
};
